import React, { useContext, useState } from 'react';
import {
    createStyles,
    Theme,
    withStyles,
    WithStyles,
} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import TreeView from '../TreeView';
import { FileSystemContext } from '../FileSystem';
import apiUrl from '../../assets/apiUrl';
import AuthContext, { getAccessToken } from '../Auth';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0,
            padding: theme.spacing(2),
        },
        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
        },
    });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

interface Props {
    id?: string;
    open: boolean;
    onClose: () => void;
    isFile: boolean;
}

const MoveTo: React.FC<Props> = ({ id, open, onClose, isFile }) => {
    const [files, setFiles] = useContext(FileSystemContext).useFiles();
    const [folders, setFolders] = useContext(FileSystemContext).useFolders();
    const { getFolderTree } = useContext(FileSystemContext).useFoldersMethods();

    const { fetchWrapper } = useContext(AuthContext);
    const handleClose = () => {
        onClose();
    };
    const move = async (
        originalFolder: string,
        moveToFolder: string | undefined
    ) => {
        if (moveToFolder === undefined) {
            return;
        }
        const token = await getAccessToken();
        if (isFile) {
            await fetchWrapper(
                `${apiUrl}/move/${originalFolder}?move_to=${moveToFolder}`,
                {
                    method: 'PUT',
                    headers: { 'x-access-token': token },
                }
            );
            let newArr = [...files];
            const index = files.findIndex((x) => x.file_uid === originalFolder);
            newArr[index] = {
                ...newArr[index],
                folder_uid: moveToFolder,
            };
            setFiles(newArr);
            onClose();
            return;
        }
        try {
            const originFolder = folders.find(
                (e) => e.folder_uid === originalFolder
            );
            const toMoveFolder = folders.find(
                (e) => e.folder_uid === moveToFolder
            );

            await fetchWrapper(
                `${apiUrl}/move/${originFolder?.folder_uid}?move_to=${toMoveFolder?.folder_uid}`,
                { method: 'PUT', headers: { 'x-access-token': token } }
            );
            if (originFolder?.folder_uid === toMoveFolder?.parent_folder) {
                await fetchWrapper(
                    `${apiUrl}/move/${toMoveFolder?.folder_uid}`,
                    {
                        method: 'PUT',
                        headers: { 'x-access-token': token },
                    }
                );
            }

            // update state
            const index = folders.findIndex(
                (x) => x.folder_uid === originalFolder
            );
            let newArr = [...folders];
            newArr[index] = {
                ...newArr[index],
                parent_folder: moveToFolder,
            };
            if (originFolder?.folder_uid === toMoveFolder?.parent_folder) {
                const i = folders.findIndex(
                    (x) => x.folder_uid === toMoveFolder?.folder_uid
                );
                newArr[i] = {
                    ...newArr[i],
                    parent_folder: null,
                };
            }
            setFolders(newArr);
            onClose();
        } catch (error) {
            console.log('error during move');
        }
    };
    const [selectedFolder, setSelectedFolder] = useState(id);
    return (
        <div>
            {typeof id !== 'undefined' && (
                <Dialog
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                >
                    <DialogTitle
                        id="customized-dialog-title"
                        onClose={handleClose}
                    >
                        Move To...
                    </DialogTitle>
                    <DialogContent dividers>
                        <TreeView
                            paths={getFolderTree(folders)}
                            onSelect={(id) =>
                                setSelectedFolder(
                                    typeof id === 'number' ? id.toString() : id
                                )
                            }
                            hide={[id]}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            autoFocus
                            onClick={() => move(id, selectedFolder)}
                            color="primary"
                        >
                            Move
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default MoveTo;
